import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Fieldset } from 'primereact/fieldset';
import { ToggleButton } from 'primereact/togglebutton';
import { useQuery } from '@apollo/react-hooks';
import _ from 'lodash';
import { getUserLanguage } from '../../../utils/LanguageUtil';
import RespondentEditAriFieldsModal from './editAriAnswers/RespondentEditAriFieldsModal';
import { AccountContext } from '../../../components/AccountContext';
import { USER_AGREEMENT } from '../../Accounts/ARI/queries';
import { Loading } from '../../../components/Loading';
import SubmitDeclarationModal from '../../Accounts/ARI/SubmitDeclarationModal';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';
import { RespondentType } from '../../../shared/Constants';

const messages = defineMessages({
  internal: { id: 'attrs.candidateType.INTERNAL', defaultMessage: 'Internal Employee' },
  external: { id: 'attrs.candidateType.EXTERNAL', defaultMessage: 'External Respondent' },
  anonymous: { id: 'attrs.candidateType.ANONYMOUS', defaultMessage: 'Anonymous Respondent' },
  unknown: { id: 'attrs.candidateType.unknown', defaultMessage: 'Unknown' },
});

const genderMessages = defineMessages({
  U: { id: 'attrs.gender.U', defaultMessage: 'Unsepcified' },
  M: { id: 'attrs.gender.M', defaultMessage: 'Male' },
  F: { id: 'attrs.gender.F', defaultMessage: 'Female' },
});

const ariMessages = defineMessages({
  topLevelFieldsetLegend: {
    id: 'ari.respondent.display.header',
    defaultMessage: 'Additional Respondent Information (ARI)',
  },
  globalFieldsetLegend: {
    id: 'ari.respondent.display.global.header',
    defaultMessage: 'Global',
  },
  localFieldsetLegend: {
    id: 'ari.respondent.display.local.header',
    defaultMessage: 'Local',
  },
  noInformationAvailable: {
    id: 'ari.respondent.display.no.information',
    defaultMessage: 'No information available.',
  },
  updateAnswers: {
    id: 'ari.respondent.display.update.answers',
    defaultMessage: 'Update answers',
  },
});

const genderMessage = (gender) => {
  switch (gender) {
    case 'M':
      return genderMessages.M;
    case 'F':
      return genderMessages.F;
    default:
      return genderMessages.U;
  }
};

const respondentTypeTextId = (respondent) => {
  switch (respondent.type) {
    case RespondentType.INTERNAL:
      return messages.internal;
    case RespondentType.EXTERNAL:
      return messages.external;
    case RespondentType.ANONYMOUS:
      return messages.anonymous;
    default:
      return messages.unknown;
  }
};

const RespondentDisplay = ({ availableTranslations, toggleEdit, respondent }) => {
  const [ariEditModalVisible, setAriEditModalVisible] = useState(false);
  const [agreed, setAgreed] = useState(null);

  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const { data: agreement, refetch: refetchAgreement } = useQuery(USER_AGREEMENT, { variables: { first: 999 } });

  useEffect(() => {
    refetchAgreement();
    if (agreement && agreement.viewer) {
      const accountEdge = _.find(agreement.viewer.accounts.edges, (edge) => edge.node.id === currentAccount);
      setAgreed(accountEdge.agreedToGdprTerms);
    }
  }, [agreement, refetchAgreement, currentAccount]);

  const locale = getUserLanguage(respondent.locale, availableTranslations);
  const respondentType = formatMessage(respondentTypeTextId(respondent));

  return agreed === null ? (
    <Loading />
  ) : (
    <>
      <Panel
        header={
          <div className="p-grid p-nogutter">
            <div className="p-col">
              {respondent.firstName} {respondent.lastName}
            </div>
            {!respondent.anonymizedOn && (
              <div className="p-col-2 align-right">
                <Button
                  icon="fa fa-pencil"
                  type="button"
                  className="p-button-secondary p-button-sm"
                  disabled={subscriptionIsExpired}
                  onClick={() => toggleEdit('core')}
                />
              </div>
            )}
          </div>
        }
      >
        <div className="panel panel-no-background">
          <div className="panel-body">
            {respondent.gender !== 'U' ? (
              <div className="details">
                <span>
                  <FormattedMessage id="attrs.gender" defaultMessage="Gender" />
                </span>
                <span>
                  {': '}
                  {formatMessage(genderMessage(respondent.gender))}
                </span>
              </div>
            ) : null}
            <div className="details">
              <span>
                <FormattedMessage id="attrs.locale" defaultMessage="Preferred Language" />
              </span>
              <span>
                {': '}
                {locale && locale.label}
              </span>
            </div>
            <div className="details">
              <span>
                <FormattedMessage id="attrs.candidateType" defaultMessage="Respondent type" />
              </span>
              <span>
                {': '}
                {respondentType}
              </span>
            </div>
            <div className="details">
              <span>
                <FormattedMessage id="attrs.email" defaultMessage="Email" />
              </span>
              <span>
                {': '}
                {respondent.email}
              </span>
            </div>

            {respondent.phoneNumber && (
              <div className="details">
                <span>
                  <FormattedMessage id="attrs.phoneNumber" defaultMessage="Phone number" />
                </span>
                <span>
                  {': '}
                  {respondent.phoneNumber}
                </span>
              </div>
            )}

            <div className="details">
              <span>
                <FormattedMessage id="attrs.notes" defaultMessage="Notes" />
              </span>
              {respondent.notes ? (
                <>
                  :<pre>{respondent.notes}</pre>
                </>
              ) : (
                <FormattedMessage id="attrs.notes.empty" defaultMessage="(no notes)">
                  {(text) => (
                    <span>
                      {': '}
                      {text}
                    </span>
                  )}
                </FormattedMessage>
              )}
            </div>

            <Fieldset legend={formatMessage(ariMessages.topLevelFieldsetLegend)} style={{ marginTop: '10px' }}>
              <Fieldset legend={formatMessage(ariMessages.globalFieldsetLegend)}>
                {respondent.globalAriFields.length === 0 ? (
                  <p>{formatMessage(ariMessages.noInformationAvailable)}</p>
                ) : (
                  <ul style={{ listStyle: 'none', padding: '0px' }}>
                    {respondent.globalAriFields.map((field) => (
                      <li style={{ borderBottom: '1px dashed black', marginBottom: '5px' }} key={field.question}>
                        <div className="p-grid">
                          <div
                            className="p-col-5"
                            style={{ fontStyle: 'italic', fontWeight: 'bold', textAlign: 'right' }}
                          >
                            {field.question}
                          </div>
                          <div className="p-col-2" style={{ textAlign: 'center' }}>
                            |
                          </div>
                          <div className="p-col-5" style={{ textAlign: 'left' }}>
                            {field.answer === '' ? 'N/A' : field.answer}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Fieldset>

              <br />

              <Fieldset legend={formatMessage(ariMessages.localFieldsetLegend)}>
                {respondent.localAriFields.length === 0 ? (
                  <p>{formatMessage(ariMessages.noInformationAvailable)}</p>
                ) : (
                  <ul style={{ listStyle: 'none', padding: '0px' }}>
                    {respondent.localAriFields.map((field) => (
                      <li style={{ borderBottom: '1px dashed black', marginBottom: '5px' }} key={field.question}>
                        <div className="p-grid">
                          <div
                            className="p-col-5"
                            style={{ fontStyle: 'italic', fontWeight: 'bold', textAlign: 'right' }}
                          >
                            {field.question}
                          </div>
                          <div className="p-col-2" style={{ textAlign: 'center' }}>
                            |
                          </div>
                          <div className="p-col-5" style={{ textAlign: 'left' }}>
                            {field.answer === '' ? 'N/A' : field.answer}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Fieldset>

              <div className="p-grid p-justify-center" style={{ marginTop: '5px' }}>
                <div className="col-12">
                  <ToggleButton
                    icon="pi pi-pencil"
                    className="p-button-warning"
                    checked={ariEditModalVisible}
                    onChange={(e) => {
                      setAriEditModalVisible(e.value);
                    }}
                    offLabel={formatMessage(ariMessages.updateAnswers)}
                    onLabel="..."
                  />
                </div>
              </div>
            </Fieldset>
          </div>
        </div>
        {ariEditModalVisible && agreed && (
          <RespondentEditAriFieldsModal
            isVisible={ariEditModalVisible}
            setIsVisible={setAriEditModalVisible}
            respondent={respondent}
          />
        )}
        {ariEditModalVisible && agreed === false && (
          <SubmitDeclarationModal
            isVisible={!agreed}
            currentAccount={currentAccount}
            onCancel={() => {
              setAriEditModalVisible(false);
            }}
          />
        )}
      </Panel>
    </>
  );
};

RespondentDisplay.propTypes = {
  availableTranslations: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleEdit: PropTypes.func.isRequired,
  respondent: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default RespondentDisplay;
